import { router, Stack } from 'expo-router';
import * as React from 'react';
import { View } from 'react-native';

import { ProfileEditScreen } from '@/components/profiles/profile-edit-screen';
import { useTenant } from '@/components/tenant-provider';
import {
  normalizeProfileLinks,
  profileLinksToText,
  updateTenantProfile,
  uploadTenantAvatar,
  type TenantProfile,
} from '@/lib/tenant-profile';
import { useReleaseFeatureFlag } from '@/lib/use-release-feature-flag';

export default function TenantProfileEditRoute() {
  const { activeTenant } = useTenant();
  const profilePhotosEnabled = useReleaseFeatureFlag('profilePhotos') === true;

  const [savedTenant, setSavedTenant] = React.useState<TenantProfile | null>(null);
  const currentTenant = savedTenant ?? activeTenant;
  const [nameInput, setName] = React.useState<string | null>(null);
  const [bioInput, setBio] = React.useState<string | null>(null);
  const [linksTextInput, setLinksText] = React.useState<string | null>(null);
  const [contactEmailInput, setContactEmail] = React.useState<string | null>(null);
  const [phoneInput, setPhone] = React.useState<string | null>(null);
  const [phoneIsPublicInput, setPhoneIsPublic] = React.useState<boolean | null>(null);
  const [isSavingProfile, setIsSavingProfile] = React.useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const name = nameInput ?? currentTenant?.name ?? '';
  const bio = bioInput ?? currentTenant?.bio ?? '';
  const linksText = linksTextInput ?? profileLinksToText(currentTenant?.links ?? []);
  const contactEmail = contactEmailInput ?? currentTenant?.contact_email ?? '';
  const phone = phoneInput ?? currentTenant?.phone ?? '';
  const phoneIsPublic = phoneIsPublicInput ?? currentTenant?.phone_is_public ?? false;

  async function saveProfile() {
    if (!currentTenant) {
      return;
    }

    const nextName = name.trim();
    if (!nextName) {
      setError('Club name is required.');
      return;
    }

    setIsSavingProfile(true);
    setError(null);
    setMessage(null);
    try {
      const next = await updateTenantProfile(currentTenant.id, {
        bio: bio.trim() || null,
        contactEmail: contactEmail.trim() || null,
        links: normalizeProfileLinks(linksText),
        name: nextName,
        phone: phone.trim() || null,
        phoneIsPublic,
      });
      setSavedTenant(next);
      setName(next.name);
      setBio(next.bio ?? '');
      setLinksText(profileLinksToText(next.links));
      setContactEmail(next.contact_email ?? '');
      setPhone(next.phone ?? '');
      setPhoneIsPublic(next.phone_is_public);
      setMessage('Tenant profile saved.');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Could not save tenant profile.');
    } finally {
      setIsSavingProfile(false);
    }
  }

  async function pickAvatar() {
    if (!currentTenant) {
      return;
    }

    const ImagePicker = await import('expo-image-picker').catch(() => null);
    if (!ImagePicker) {
      setError(
        'Photo picker is unavailable in this build. Rebuild the development app and try again.'
      );
      return;
    }

    const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permission.granted) {
      setError('Allow photo library access to update the tenant profile photo.');
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      quality: 0.88,
    });
    if (result.canceled) {
      return;
    }

    setIsUploadingAvatar(true);
    setError(null);
    setMessage(null);
    try {
      const avatarUrl = await uploadTenantAvatar(currentTenant.id, result.assets[0]);
      setSavedTenant((tenant) => {
        const baseTenant = tenant ?? currentTenant;
        return baseTenant ? { ...baseTenant, avatar_url: avatarUrl } : baseTenant;
      });
      setMessage('Tenant profile photo updated.');
    } catch (avatarError) {
      setError(avatarError instanceof Error ? avatarError.message : 'Could not update photo.');
    } finally {
      setIsUploadingAvatar(false);
    }
  }

  return (
    <View className="flex-1 bg-background">
      <Stack.Screen
        options={{ headerShown: process.env.EXPO_OS !== 'web', title: 'Edit profile' }}
      />
      <Stack.Screen.BackButton displayMode="minimal">Club</Stack.Screen.BackButton>
      <ProfileEditScreen
        error={error}
        form={{
          avatarButtonLabel: 'Edit photo',
          bio,
          bioPlaceholder: 'Morning routes, sweaty logistics, and post-run coffee for the crew.',
          contactEmail,
          contactEmailPlaceholder: 'crew@6ambkk.com',
          description: 'Edit what runners see when they land on this club profile.',
          isSaving: isSavingProfile,
          isUploadingAvatar,
          linksText,
          name,
          nameLabel: 'Club name',
          namePlaceholder: '6am BKK',
          onAvatarPress: profilePhotosEnabled ? () => void pickAvatar() : undefined,
          onBioChange: setBio,
          onContactEmailChange: setContactEmail,
          onLinksTextChange: setLinksText,
          onNameChange: setName,
          onPhoneChange: setPhone,
          onPhoneIsPublicChange: setPhoneIsPublic,
          onSave: () => void saveProfile(),
          phone,
          phoneHelper: 'Public contact number for club operations.',
          phoneIsPublic,
          phoneIsPublicDescription: 'Show this number on the tenant profile.',
          phoneIsPublicLabel: 'Public phone',
          saveLabel: 'Save tenant profile',
          title: 'Tenant profile',
        }}
        message={message}
        onBack={() => router.back()}
        subtitle={currentTenant?.slug}
        title="Edit profile"
        topPadding={24}
      />
    </View>
  );
}
